/**
 * Super-admin management HTTP API wrappers.
 *
 * Served by api-server over cookie-authenticated HTTP under /api/admin,
 * guarded server-side by `requireSuperAdmin`. All calls use
 * `credentials: 'include'` so the httpOnly auth cookie is sent.
 *
 * Modelled on ../utils/api.ts (raw fetch + credentials).
 *
 * NOTE: dates arrive as ISO strings over JSON.
 */

import { API_URL } from '../config';
import type { AdminPermissions, GameType } from '@swtorbit/shared-socket-events';

/** One admin row for the management list (no secrets). */
export interface AdminSummary {
  id: number;
  username: string;
  email: string | null;
  is_super_admin: boolean;
  disabled: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  permissions: AdminPermissions;
  created_at: string;
}

/** One login-attempt audit row. */
export interface AdminLoginEvent {
  id: number;
  admin_id: number | null;
  username_attempted: string;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  created_at: string;
}

/** Read the `{ error }` body from a non-2xx response and throw. */
async function throwApiError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({ error: fallback }));
  throw new Error(data.error || fallback);
}

/**
 * Create a new admin account.
 * @endpoint POST /api/admin/admins
 */
export async function createAdmin(
  username: string,
  password: string,
  isSuperAdmin = false,
  permissions?: AdminPermissions,
): Promise<AdminSummary> {
  const response = await fetch(`${API_URL}/api/admin/admins`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, isSuperAdmin, permissions }),
  });
  if (!response.ok) await throwApiError(response, 'Failed to create admin');
  const data = await response.json();
  return data.admin as AdminSummary;
}

/**
 * List all admins.
 * @endpoint GET /api/admin/admins
 */
export async function listAdmins(): Promise<AdminSummary[]> {
  const response = await fetch(`${API_URL}/api/admin/admins`, {
    credentials: 'include',
  });
  if (!response.ok) await throwApiError(response, 'Failed to load admins');
  const data = await response.json();
  return (data.admins ?? []) as AdminSummary[];
}

/**
 * Fetch a single admin's login history (most recent first).
 * @endpoint GET /api/admin/admins/:id/login-history
 */
export async function getLoginHistory(adminId: number, limit = 100): Promise<AdminLoginEvent[]> {
  const response = await fetch(
    `${API_URL}/api/admin/admins/${adminId}/login-history?limit=${limit}`,
    { credentials: 'include' },
  );
  if (!response.ok) await throwApiError(response, 'Failed to load login history');
  const data = await response.json();
  return (data.events ?? []) as AdminLoginEvent[];
}

/**
 * Reset an admin's password.
 * @endpoint POST /api/admin/admins/:id/password
 */
export async function resetAdminPassword(adminId: number, password: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/admin/admins/${adminId}/password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) await throwApiError(response, 'Failed to reset password');
}

/**
 * Reset an admin's per-game image usage counters.
 * @endpoint POST /api/admin/admins/:id/usage/reset
 */
export async function resetAdminUsage(adminId: number, gameType: GameType): Promise<void> {
  const response = await fetch(`${API_URL}/api/admin/admins/${adminId}/usage/reset`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gameType }),
  });
  if (!response.ok) await throwApiError(response, 'Failed to reset usage counters');
}

/**
 * Enable or disable an admin account.
 * @endpoint POST /api/admin/admins/:id/{disable|enable}
 */
export async function setAdminDisabled(adminId: number, disabled: boolean): Promise<void> {
  const action = disabled ? 'disable' : 'enable';
  const response = await fetch(`${API_URL}/api/admin/admins/${adminId}/${action}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) await throwApiError(response, `Failed to ${action} admin`);
}

/**
 * Permanently delete an admin (cascades to all their games).
 * @endpoint DELETE /api/admin/admins/:id
 */
export async function deleteAdmin(adminId: number): Promise<void> {
  const response = await fetch(`${API_URL}/api/admin/admins/${adminId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) await throwApiError(response, 'Failed to delete admin');
}
